import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import {
  PERFORMANCE_RANGE_OPTIONS,
  getTargetMonths,
  fmtMillion,
  type PerformanceRange,
  type DepartmentPlRow,
} from './constants';

type Props = {
  deptPlRows: DepartmentPlRow[];
  deptPlLoading: boolean;
  performanceRange: PerformanceRange;
  currentMonth: number;
  fiscalYear: number;
};

// 部署グループごとの表示用アクセントカラー
const groupColors: Record<string, { accent: string; bg: string }> = {
  '1000': { accent: '#3b82f6', bg: '#eff6ff' }, // 木造
  '2000': { accent: '#8b5cf6', bg: '#f5f3ff' }, // マンション
  '3000': { accent: '#0ea5e9', bg: '#ecfeff' }, // 大規模改修
  '4000': { accent: '#f59e0b', bg: '#fffbeb' }, // 改修
  '5000': { accent: '#64748b', bg: '#f8fafc' }, // 本社
  '6000': { accent: '#ec4899', bg: '#fdf2f8' }, // CC
  'OTHER': { accent: '#94a3b8', bg: '#f1f5f9' }, // その他・共通
};

/**
 * 部署別 業績ダッシュボード（部署グループごとの売上高カード）。
 * 業績範囲は上段サマリーのラジオで操作され、本セクションも同じ範囲に追従する。
 */
export default function DepartmentPerformanceCards({ deptPlRows, deptPlLoading, performanceRange, currentMonth, fiscalYear }: Props) {
  // 予算達成率 (実績 / |予算| × 100) 予算0なら null
  const getAchievementRate = (actual: number, budget: number): number | null => {
    if (budget === 0) return null;
    return (actual / Math.abs(budget)) * 100;
  };
  // 前年比 (実績 / |前年| × 100) 前年0なら null
  const getYoyRate = (actual: number, prev: number): number | null => {
    if (prev === 0) return null;
    return (actual / Math.abs(prev)) * 100;
  };

  // 比率チップ (vs計画 / vs前年)。rate が null のときはグレーで「-」
  const renderRateChip = (rate: number | null, caption: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.2, px: 0.6, py: 0.15, borderRadius: '4px',
        backgroundColor: rate === null ? '#f1f5f9' : (rate >= 100 ? '#ecfdf5' : '#fef2f2'),
        color: rate === null ? '#94a3b8' : (rate >= 100 ? '#059669' : '#ef4444'),
      }}>
        {rate !== null && (rate >= 100 ? <Icons.TrendingUp sx={{ fontSize: '12px' }} /> : <Icons.TrendingDown sx={{ fontSize: '12px' }} />)}
        <Typography sx={{ fontSize: '10.5px', fontWeight: 700 }}>
          {rate === null ? '-' : `${rate.toFixed(1)}%`}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '9px', fontWeight: 500, color: '#94a3b8', lineHeight: 1, mt: 0.15 }}>
        {caption}
      </Typography>
    </Box>
  );

  // 1指標ブロック (売上高 / 営業利益) の描画
  const renderMetricBlock = (label: string, actual: number, budget: number, prev: number) => (
    <Box>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', mb: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: '19px', fontWeight: 800, color: actual < 0 ? '#ef4444' : '#1e293b', lineHeight: 1.15 }}>
              {fmtMillion(actual)}
            </Typography>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8' }}>百万</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, mt: 0.25 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1.3 }}>計画</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#64748b', lineHeight: 1.3 }}>{fmtMillion(budget)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1.3 }}>差額</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: (actual - budget) >= 0 ? '#059669' : '#ef4444', lineHeight: 1.3 }}>{(actual - budget) >= 0 ? '+' : ''}{fmtMillion(actual - budget)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1.3 }}>前年</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#64748b', lineHeight: 1.3 }}>{fmtMillion(prev)}</Typography>
            </Box>
          </Box>
        </Box>
        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
          {renderRateChip(getAchievementRate(actual, budget), 'vs計画')}
          {renderRateChip(getYoyRate(actual, prev), 'vs前年')}
        </Stack>
      </Box>
    </Box>
  );

  // 選択中の業績範囲に対応する会計月の配列 (単月/四半期/半期/通期)。期間セレクタと共有
  const deptTargetMonths = getTargetMonths(performanceRange, currentMonth);
  const deptRangeLabel =
    performanceRange === 'month'
      ? `${currentMonth}月`
      : PERFORMANCE_RANGE_OPTIONS.find((o) => o.value === performanceRange)?.label ?? '通期';
  // 月次内訳を選択範囲で合算して該当系列の売上を返す
  const sumDeptSales = (d: DepartmentPlRow, suffix: 'actual' | 'budget' | 'prev'): number => {
    const byMonth =
      suffix === 'actual' ? d.sales_actual_by_month
      : suffix === 'budget' ? d.sales_budget_by_month
      : d.sales_prev_by_month;
    if (!byMonth) {
      // 後方互換 (月次内訳未提供のレスポンス) は通期合計のみ扱う
      const fallback = suffix === 'actual' ? d.sales_actual : suffix === 'budget' ? d.sales_budget : d.sales_prev;
      return performanceRange === 'annual' ? fallback : 0;
    }
    return deptTargetMonths.reduce((s, m) => s + (Number(byMonth[m]) || 0), 0);
  };

  // 工事売上高 (実績) を選択範囲で合算して返す
  const sumDeptKoji = (d: DepartmentPlRow): number => {
    const byMonth = d.koji_actual_by_month;
    if (!byMonth) {
      // 後方互換 (月次内訳未提供のレスポンス) は通期合計のみ扱う
      return performanceRange === 'annual' ? (d.koji_actual ?? 0) : 0;
    }
    return deptTargetMonths.reduce((s, m) => s + (Number(byMonth[m]) || 0), 0);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* 業績範囲は上段サマリーのラジオで操作し、当セクションも同じ範囲に追従する */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.AccountTree sx={{ color: blueGrey[400] }} /> 部署別 {deptRangeLabel}業績
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#64748b' }}>
          {fiscalYear}年度 {deptRangeLabel} / 売上高（実績 vs 計画・前年）
        </Typography>
      </Box>

      {/* ⏳ 部署別データの読み込み */}
      {deptPlLoading && <LoadingView message="部署別データ取得中..." height="30vh" size={50} />}

      {/* 実績がゼロの部署カードは非表示 (活動なしの部署を除外) */}
      {(() => {
        const visibleDeptRows = deptPlRows.filter(d => sumDeptSales(d, 'actual') !== 0);
        // 売上構成割合の分母 (全社=全部署の売上実績合計。拠点絞り込み時は対象拠点合計)
        const totalSalesActual = deptPlRows.reduce((s, d) => s + sumDeptSales(d, 'actual'), 0);
        if (deptPlLoading) return null;
        if (visibleDeptRows.length === 0) {
          return (
            <Box sx={{ p: 3, border: '2px dashed #cbd5e1', borderRadius: '12px' }}>
              <Typography color="textSecondary" sx={{ fontSize: '14px' }}>
                実績が計上された部署がありません。
              </Typography>
            </Box>
          );
        }
        return (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
        }}>
          {visibleDeptRows.map(d => {
            const c = groupColors[d.department_group] || groupColors['OTHER'];
            const salesActual = sumDeptSales(d, 'actual');
            const salesBudget = sumDeptSales(d, 'budget');
            const salesPrev = sumDeptSales(d, 'prev');
            // 売上構成割合 (当部署売上実績 / 全社売上実績)
            const share = totalSalesActual !== 0 ? (salesActual / totalSalesActual) * 100 : null;
            // 工事高 (工事売上高実績) と、当部署売上に占める工事高比率
            const kojiActual = sumDeptKoji(d);
            const kojiRate = salesActual !== 0 ? (kojiActual / salesActual) * 100 : null;

            return (
              <Card key={d.department_group} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                {/* 部署名ヘッダー (アクセントバー付き)。右側に売上構成割合 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.75, py: 1, backgroundColor: c.bg, borderLeft: `4px solid ${c.accent}` }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#1e293b', minWidth: 0, lineHeight: 1.25 }}>
                    {d.department_name}
                  </Typography>
                  {share !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#94a3b8', mr: 0.2 }}>構成比</Typography>
                      <Typography sx={{ fontSize: '15px', fontWeight: 800, color: c.accent, lineHeight: 1 }}>
                        {share.toFixed(1)}
                      </Typography>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: c.accent }}>%</Typography>
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ px: 1.75, py: 1.75, '&:last-child': { pb: 1.75 } }}>
                  {renderMetricBlock('売上高', salesActual, salesBudget, salesPrev)}
                  {/* 下段: 工事高 (工事売上高実績) の額面と当部署売上に占める割合。工事高ゼロの部署では非表示 */}
                  {kojiActual !== 0 && (
                    <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', flexShrink: 0 }}>
                        工事高
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '15px', fontWeight: 800, color: kojiActual < 0 ? '#ef4444' : '#1e293b', lineHeight: 1.15 }}>
                          {fmtMillion(kojiActual)}
                        </Typography>
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#94a3b8' }}>百万</Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: c.accent, ml: 0.25 }}>
                          ({kojiRate === null ? '-' : `${kojiRate.toFixed(1)}%`})
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
        );
      })()}
    </Box>
  );
}
